import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Loader2, Check, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" {...props}>
        <path
            fill="currentColor"
            d="M21.35 11.1H12v3.83h5.34c-.23 1.4-1.66 4.1-5.34 4.1-3.21 0-5.83-2.66-5.83-5.94S8.79 7.16 12 7.16c1.83 0 3.05.78 3.75 1.45l2.56-2.46C16.7 4.7 14.55 3.8 12 3.8 6.95 3.8 2.86 7.88 2.86 12.94S6.95 22.08 12 22.08c6.94 0 9.36-4.86 9.36-7.34 0-.49-.05-.86-.11-1.25z"
        />
    </svg>
);

export default function LoginDialog({ open, onOpenChange }) {
    const { startGoogleLogin, requestMagicLink, clearStuckSession } = useAuth();
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const reset = () => {
        setEmail("");
        setSending(false);
        setSent(false);
    };

    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const handleMagicLink = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSending(true);
        try {
            await requestMagicLink(email);
            setSent(true);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send sign-in link.";
            toast.error("Sign-in failed", { description: detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="login-dialog"
                className="bg-zinc-950 border border-zinc-800 rounded-none max-w-md p-0 overflow-hidden"
            >
                <div className="h-1 bg-brand-red w-full" />
                <div className="p-8">
                    {!sent ? (
                        <>
                            <DialogHeader className="text-left space-y-3">
                                <div className="text-[10px] tracking-[0.25em] text-brand-red uppercase">
                                    Member Access
                                </div>
                                <DialogTitle className="font-display text-4xl tracking-wider text-white">
                                    SIGN IN OR CREATE ACCOUNT.
                                </DialogTitle>
                                <DialogDescription className="text-zinc-200 text-sm leading-relaxed">
                                    One door for new members and returning athletes. Use Google or get a magic link sent
                                    to your email — no passwords.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="mt-7 space-y-3">
                                <button
                                    type="button"
                                    data-testid="login-google-button"
                                    onClick={startGoogleLogin}
                                    className="w-full btn-tactical justify-center"
                                >
                                    <GoogleIcon />
                                    Continue with Google
                                </button>

                                <div className="relative my-5 flex items-center">
                                    <span className="flex-1 h-px bg-zinc-900" />
                                    <span className="px-3 text-[10px] tracking-[0.25em] uppercase text-zinc-200">
                                        Or
                                    </span>
                                    <span className="flex-1 h-px bg-zinc-900" />
                                </div>

                                <form onSubmit={handleMagicLink} className="space-y-3">
                                    <label
                                        htmlFor="login-email"
                                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block"
                                    >
                                        Email
                                    </label>
                                    <input
                                        id="login-email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="login-email-input"
                                        placeholder="you@example.com"
                                        className="w-full bg-black border border-zinc-800 px-4 py-3 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red transition-colors"
                                    />
                                    <button
                                        type="submit"
                                        disabled={sending || !email}
                                        data-testid="login-magic-link-button"
                                        className="w-full btn-ghost-tactical justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {sending ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Mail className="w-4 h-4" />
                                        )}
                                        {sending ? "Sending…" : "Email me a sign-in link"}
                                    </button>
                                </form>

                                <p className="text-[11px] text-zinc-300 leading-relaxed pt-3">
                                    By continuing you agree to our terms. New members will complete a quick waiver and
                                    profile after their first sign-in.
                                </p>

                                <button
                                    type="button"
                                    data-testid="login-clear-stuck-session"
                                    onClick={async () => {
                                        try {
                                            await clearStuckSession();
                                            toast.success("Cleared. Try signing in again.");
                                        } catch {
                                            toast.error("Couldn't clear session — try clearing cookies in your browser.");
                                        }
                                    }}
                                    className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 hover:text-brand-red transition-colors pt-1"
                                >
                                    Stuck signing in? Reset & try again
                                </button>
                                <a
                                    href="/auth/diagnose"
                                    data-testid="login-diagnose-link"
                                    className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-brand-red transition-colors"
                                >
                                    Run a diagnostic →
                                </a>
                            </div>
                        </>
                    ) : (
                        <div data-testid="login-sent-state">
                            <DialogHeader className="text-left space-y-3">
                                <div className="text-[10px] tracking-[0.25em] text-brand-red uppercase">
                                    Check your inbox
                                </div>
                                <DialogTitle className="font-display text-4xl tracking-wider text-white">
                                    LINK SENT.
                                </DialogTitle>
                                <DialogDescription className="text-zinc-200 text-sm leading-relaxed">
                                    We just emailed a one-time sign-in link to{" "}
                                    <span className="text-white">{email}</span>. It expires in 15 minutes.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="mt-7 flex items-center gap-3 border border-zinc-900 bg-black p-4">
                                <span className="inline-flex items-center justify-center w-9 h-9 bg-brand-red/10 border border-brand-red/40 text-brand-red flex-shrink-0">
                                    <Check className="w-4 h-4" />
                                </span>
                                <div className="text-sm text-zinc-300">
                                    Open the email and tap <span className="text-white font-semibold">Sign in</span>.
                                    Don't see it? Check spam.
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    data-testid="login-sent-back"
                                    onClick={() => setSent(false)}
                                    className="btn-ghost-tactical justify-center"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Use different email
                                </button>
                                <button
                                    type="button"
                                    data-testid="login-sent-close"
                                    onClick={() => handleClose(false)}
                                    className="btn-tactical justify-center"
                                >
                                    Got it
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
